import React from "react";
import { useNavigate } from "react-router-dom";
import { Lightning } from "@phosphor-icons/react";
import { PlayerTag, QualityBadge, TypeBadge, StatusPill } from "./badges";

export default function ChallengeCard({ challenge, onClick, index = 0 }) {
    const c = challenge;
    const isMarketDecision = c.challengeType === "MARKET_DECISION";
    const navigate = useNavigate();
    return (
        <button
            onClick={() => onClick?.(c)}
            className="relative group text-left bg-[#121212] border border-white/10 hover:border-white/30 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
            data-testid={`challenge-card-${c.id}`}
            style={{ animationDelay: `${index * 40}ms` }}
        >
            {/* Top data strip */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-[#0a0a0a]">
                <div className="flex items-center gap-2">
                    <TypeBadge type={c.challengeType} />
                    <StatusPill status={c.status} />
                </div>
                <QualityBadge score={c.qualityScore} />
            </div>

            {/* Body */}
            <div className="p-5 space-y-3">
                <h3 className="font-display text-lg md:text-xl font-bold uppercase tracking-tight leading-tight text-white">
                    {c.title}
                </h3>
                <p className="text-sm text-white/70 leading-relaxed line-clamp-2">
                    {c.shortDescription}
                </p>

                {c.matchContext && (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cyan flex items-center gap-2">
                        <span className="w-1 h-1 bg-fa-cyan rounded-full" />
                        {c.matchContext}
                    </div>
                )}

                {/* Player chips (first 4) */}
                {c.players?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
                        {c.players.slice(0, 4).map((p) => (
                            <div
                                key={p.id}
                                className="font-mono text-[10px] bg-black/60 border border-white/10 px-1.5 py-0.5 flex items-center gap-1.5"
                            >
                                <span className="text-white/60 uppercase">{p.team}</span>
                                <span className="text-white font-bold uppercase">{p.name}</span>
                            </div>
                        ))}
                        {c.players.length > 4 && (
                            <div className="font-mono text-[10px] text-white/40 px-1.5 py-0.5">
                                +{c.players.length - 4}
                            </div>
                        )}
                    </div>
                )}

                {/* Tag collage */}
                <div className="flex flex-wrap gap-1">
                    {Array.from(new Set(c.players?.flatMap((p) => p.tags || []) || [])).slice(0, 4).map((t) => (
                        <PlayerTag key={t} tag={t} />
                    ))}
                </div>
            </div>

            {/* Footer strip */}
            <div className="px-4 py-2 border-t border-white/5 bg-[#0a0a0a] flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {typeof c.duration === "object" && c.duration
                        ? `GW${c.duration.fromGameweek}–${c.duration.toGameweek}`
                        : (c.duration || "—")}
                </div>
                {isMarketDecision ? (
                    <span
                        role="link"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); navigate(`/play/market/${c.id}`); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate(`/play/market/${c.id}`); } }}
                        data-testid={`challenge-play-${c.id}`}
                        className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow flex items-center gap-1 hover:text-white cursor-pointer"
                    >
                        <Lightning size={10} weight="fill" /> Play →
                    </span>
                ) : (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                        Open →
                    </div>
                )}
            </div>
        </button>
    );
}
